/**
 * Shared parent-child wrapper for assignment flows that combine:
 * - a shared select control for choosing records
 * - a shared table preview for already selected records
 *
 * PARENT-CHILD CONTRACT:
 * Parents own the authoritative selected IDs and row data.
 * This wrapper only normalizes emitted selections and forwards row actions.
 */
import { ChangeDetectionStrategy, Component, input, output } from '@angular/core';

import { UiSelectComponent } from '../ui-select/ui-select.component';
import { UiSelectOption } from '../ui-select/ui-select.types';
import { UiTableComponent } from '../ui-table/ui-table.component';
import { UiTableAction, UiTableActionClick, UiTableBadgeRule, UiTableColumn } from '../ui-table/ui-table.types';
import { UiEmptyStateComponent } from '../ui-empty-state/ui-empty-state.component';
import { UiLabelTranslatePipe } from '../ui-label-translate.pipe';

/**
 * COMPONENT: Ui Assignment Selector
 * Inputs: Uses the parameters, injected services, route state, or signal state declared by this file.
 * Output: Returns the documented value or updates component/service state without changing the public contract.
 * Processing: Documents the Angular declaration that connects this class to templates, dependency injection, or template transforms.
 */
@Component({
  selector: 'ui-assignment-selector',
  standalone: true,
  imports: [UiSelectComponent, UiTableComponent, UiEmptyStateComponent, UiLabelTranslatePipe],
  templateUrl: './ui-assignment-selector.component.html',
  styleUrl: './ui-assignment-selector.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class UiAssignmentSelectorComponent {
  // PARENT-CHILD CONTRACT: parents provide labels, options, and selected IDs.
  readonly label = input('');
  /** PARENT-CHILD CONTRACT: required documents the input state boundary used by this file. */
  readonly required = input(false);
  /** PARENT-CHILD CONTRACT: single documents the input state boundary used by this file. */
  readonly single = input(false);
  readonly values = input<string[]>([]);
  readonly options = input<UiSelectOption[]>([]);
  /** PARENT-CHILD CONTRACT: error documents the input state boundary used by this file. */
  readonly error = input('');
  /** PARENT-CHILD CONTRACT: hint documents the input state boundary used by this file. */
  readonly hint = input('');
  readonly selectedRows = input<unknown[]>([]);
  readonly columns = input<UiTableColumn[]>([]);
  /** PARENT-CHILD CONTRACT: emptyMessage documents the input state boundary used by this file. */
  readonly emptyMessage = input('No records selected yet.');
  /** PARENT-CHILD CONTRACT: emptyStateTitle documents the input state boundary used by this file. */
  readonly emptyStateTitle = input('shared.table.noData');
  /** PARENT-CHILD CONTRACT: emptyStateDescription documents the input state boundary used by this file. */
  readonly emptyStateDescription = input('shared.emptyState.selectToPreview');
  readonly rowActions = input<UiTableAction[]>([]);
  readonly badgeRules = input<Record<string, UiTableBadgeRule> | null>(null);
  /** PARENT-CHILD CONTRACT: showPagination documents the input state boundary used by this file. */
  readonly showPagination = input(false);
  /** PARENT-CHILD CONTRACT: defaultPageSize documents the input state boundary used by this file. */
  readonly defaultPageSize = input(10);
  /** PARENT-CHILD CONTRACT: loadMoreOnScroll documents the input state boundary used by this file. */
  readonly loadMoreOnScroll = input(false);
  /** PARENT-CHILD CONTRACT: loadingMoreOptions documents the input state boundary used by this file. */
  readonly loadingMoreOptions = input(false);
  /** PARENT-CHILD CONTRACT: hasMoreOptions documents the input state boundary used by this file. */
  readonly hasMoreOptions = input(false);

  readonly valuesChange = output<string[]>();
  readonly actionClick = output<UiTableActionClick>();
  readonly loadMoreOptions = output<void>();

  /**
   * Normalizes the single-value select output into the array contract used by the parent.
   */
  onValueChange(value: string): void {
    this.valuesChange.emit(value ? [value] : []);
  }

  /**
   * Keeps the emitted value shape consistent for both single and multi assignment flows.
   * BUSINESS LOGIC: in single mode only the last chosen record is kept active.
   */
  onValuesChange(values: string[]): void {
    // In single mode keep only the last selected value so the dropdown acts as a single-select.
    this.valuesChange.emit(this.single() && values.length > 1 ? [values[values.length - 1]] : values);
  }

  /**
   * Forwards table row actions so the parent decides how to remove or inspect assignments.
   */
  onActionClick(event: UiTableActionClick): void {
    this.actionClick.emit(event);
  }

  /**
   * Forwards dropdown paging requests so feature parents keep API pagination state.
   */
  onLoadMoreOptions(): void {
    this.loadMoreOptions.emit();
  }
}
